import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Callout } from '../../../../shared/components/ui/primitives';

/**
 * One daily update: what got done, what is next, and — called out, because it
 * is what a manager can act on — anything blocking them.
 *
 * `showPerson` for a team feed; off on an employee's own record, where every
 * card is the same person and the date is the useful heading.
 */
const DailyUpdateCard = ({ update, showPerson = true, highlighted = false }) => (
  <article
    className={`rounded-lg border bg-white p-4 ${
      highlighted ? 'border-brand-600 ring-1 ring-brand-600' : 'border-gray-200'
    }`}
  >
    <header className="flex items-start justify-between gap-3">
      {showPerson ? (
        <div className="min-w-0">
          <div className="truncate font-medium text-gray-900">
            {update.user?.name ?? `#${update.user_id}`}
          </div>
          {update.user?.designation && (
            <div className="truncate text-xs text-gray-500">{update.user.designation}</div>
          )}
        </div>
      ) : (
        <div className="font-medium text-gray-900">{formatDay(update.date)}</div>
      )}
      <span className="shrink-0 text-xs text-gray-400">{formatTime(update.submitted_at)}</span>
    </header>

    <dl className="mt-3 space-y-2 text-sm">
      <Part label="Done today">{update.done}</Part>
      {update.next_plan && <Part label="Next">{update.next_plan}</Part>}
    </dl>

    {update.blockers && (
      <Callout tone="warning" icon={<AlertTriangle size={14} />} className="mt-3">
        <span className="font-medium">Blocker: </span>
        <span className="whitespace-pre-line">{update.blockers}</span>
      </Callout>
    )}
  </article>
);

const Part = ({ label, children }) => (
  <div>
    <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</dt>
    <dd className="whitespace-pre-line text-gray-800">{children}</dd>
  </div>
);

export function formatDay(value) {
  const key = String(value || '').slice(0, 10);
  const [y, m, d] = key.split('-').map(Number);
  if (!y || !m || !d) return '—';
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', {
    weekday: 'short',
    day: '2-digit',
    month: 'short'
  });
}

export function formatTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}

export default DailyUpdateCard;
